import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { AN_COLORS, AN_FONT } from "../../design-system/tokens";

// The design language of every page under Settings (inside SettingsShell),
// modelled on a browser's settings pages:
//
//   SettingsPage     title + one-line description, optional actions on the right
//   SettingsSection  a heading (+ description, + actions) over a white card
//   SettingsCard     the white, rounded, hairline-bordered card
//   SettingsRow      one row in a card: icon · title + description · value/control · ›
//   SettingsStatus   a small status line (dot + text), e.g. "Active · Syslog"
//   SettingsField    a labelled form field inside a card (label, hint, control, help/error)
//
// Rules: no eyebrows above the page title; sections, not nested cards; rows
// separated by a hairline; one primary button per page (in the page actions or
// at the end of the form); muted text for descriptions; status in a
// SettingsStatus, never as coloured body text.

export const SETTINGS_TOKENS = {
  cardRadius: 12,
  rowPad: "16px 20px",
  hairline: AN_COLORS.line2 || "var(--an-line2)",
};

export function SettingsPage({ title, description, actions, children }) {
  return (
    <div style={{ fontFamily: AN_FONT, color: AN_COLORS.ink, display: "grid", gap: 24 }}>
      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, maxWidth: 760 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4 }}>{title}</h1>
          {description && <p style={{ margin: "6px 0 0", fontSize: 13.5, color: AN_COLORS.muted, lineHeight: 1.5 }}>{description}</p>}
        </div>
        {actions && <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>{actions}</div>}
      </header>
      {children}
    </div>
  );
}

export function SettingsSection({ title, description, actions, children, bare = false }) {
  return (
    <section style={{ display: "grid", gap: 10 }}>
      {(title || actions) && (
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            {title && <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: AN_COLORS.ink }}>{title}</h2>}
            {description && <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 3, lineHeight: 1.45 }}>{description}</div>}
          </div>
          {actions && <div style={{ display: "flex", alignItems: "center", gap: 8 }}>{actions}</div>}
        </div>
      )}
      {bare ? children : <SettingsCard>{children}</SettingsCard>}
    </section>
  );
}

export function SettingsCard({ children, padding = 0, style }) {
  return (
    <div
      style={{
        background: AN_COLORS.surface,
        border: `1px solid ${AN_COLORS.line}`,
        borderRadius: SETTINGS_TOKENS.cardRadius,
        overflow: "hidden",
        padding,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function SettingsRow({ icon, title, description, value, control, onClick, last = false, danger = false }) {
  const [hover, setHover] = useState(false);
  const clickable = typeof onClick === "function";
  const Tag = clickable ? "button" : "div";
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        width: "100%",
        padding: SETTINGS_TOKENS.rowPad,
        textAlign: "left",
        background: clickable && hover ? AN_COLORS.bg : "transparent",
        border: "none",
        borderBottom: last ? "none" : `1px solid ${SETTINGS_TOKENS.hairline}`,
        cursor: clickable ? "pointer" : "default",
        fontFamily: AN_FONT,
      }}
    >
      {icon && (
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: 9,
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: danger ? "var(--an-dangerBg)" : AN_COLORS.primaryL,
            color: danger ? AN_COLORS.danger : AN_COLORS.primary,
          }}
        >
          {icon}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 14.5, fontWeight: 600, color: danger ? AN_COLORS.danger : AN_COLORS.ink }}>{title}</span>
        {description && (
          <span style={{ display: "block", fontSize: 12.5, color: AN_COLORS.muted, marginTop: 2, lineHeight: 1.45 }}>{description}</span>
        )}
      </span>
      {value != null && <span style={{ flexShrink: 0, maxWidth: 320, textAlign: "right", fontSize: 13, color: AN_COLORS.ink2 }}>{value}</span>}
      {control && <span style={{ flexShrink: 0 }}>{control}</span>}
      {clickable && <ChevronRight size={16} color={hover ? AN_COLORS.primary : AN_COLORS.muted} style={{ flexShrink: 0 }} />}
    </Tag>
  );
}

const STATUS_TONE = {
  success: AN_COLORS.success,
  warn: AN_COLORS.warn,
  danger: AN_COLORS.danger,
  neutral: AN_COLORS.muted,
};

export function SettingsStatus({ tone = "neutral", children }) {
  const color = STATUS_TONE[tone] || STATUS_TONE.neutral;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 500, color: tone === "neutral" ? AN_COLORS.muted : color }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
      {children}
    </span>
  );
}

export function SettingsField({ label, hint, required, help, error, children, last = false }) {
  return (
    <div style={{ padding: SETTINGS_TOKENS.rowPad, borderBottom: last ? "none" : `1px solid ${SETTINGS_TOKENS.hairline}` }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: AN_COLORS.ink }}>
          {label}
          {required && <span style={{ color: AN_COLORS.danger }}> *</span>}
        </span>
        {hint && <span style={{ fontSize: 12, color: AN_COLORS.muted }}>{hint}</span>}
      </div>
      {children}
      {(error || help) && (
        <div style={{ fontSize: 12, marginTop: 6, color: error ? AN_COLORS.danger : AN_COLORS.muted, lineHeight: 1.45 }}>{error || help}</div>
      )}
    </div>
  );
}
